import React from "react";
import { Megaphone, X } from "lucide-react";
import { useDashTheme } from "../hooks/useDashTheme";
import { useActiveAnnouncement } from "../hooks/useActiveAnnouncement";

/**
 * Latest main_admin announcement. org_admin close = gone for this org
 * until a newer post lands. No sockets — refresh picks up the next one.
 */
export function AnnouncementBanner({ style }) {
  const t = useDashTheme();
  const { announcement, canDismiss, closing, dismiss } = useActiveAnnouncement();
  if (!announcement) return null;

  const when = announcement.createdAt
    ? new Date(announcement.createdAt).toLocaleDateString(undefined, {
      month: "short", day: "numeric",
    })
    : "";

  return (
    <aside
      style={{
        position: "relative",
        overflow: "hidden",
        borderRadius: 18,
        padding: "18px 20px 18px 18px",
        background: t.isDark
          ? "linear-gradient(135deg, rgba(109,74,255,.22) 0%, rgba(37,39,40,1) 42%, rgba(66,115,209,.16) 100%)"
          : "linear-gradient(135deg, #EEF1FF 0%, #FFFFFF 46%, #E8F1FE 100%)",
        border: `1px solid ${t.isDark ? "rgba(109,74,255,.35)" : "#D5DCF3"}`,
        boxShadow: t.shadow?.sm || "0 2px 8px rgba(28,32,55,.06)",
        opacity: closing ? 0 : 1,
        transform: closing ? "translateY(-6px)" : "none",
        transition: "opacity 180ms ease, transform 180ms ease",
        ...style,
      }}
    >
      <span aria-hidden style={{
        position: "absolute", right: -18, top: -22, width: 110, height: 110, borderRadius: 99,
        background: t.isDark ? "rgba(109,74,255,.16)" : "rgba(66,115,209,.10)",
      }} />
      <div style={{ display: "flex", gap: 14, alignItems: "flex-start", position: "relative" }}>
        <span style={{
          width: 40, height: 40, borderRadius: 12, flex: "none",
          display: "grid", placeItems: "center",
          background: t.isDark ? "rgba(109,74,255,.28)" : "#4273D1",
          color: "#fff",
          boxShadow: "0 8px 18px -10px rgba(66,115,209,.7)",
        }}>
          <Megaphone size={18} strokeWidth={2.1} />
        </span>
        <div style={{ minWidth: 0, flex: 1, paddingRight: canDismiss ? 28 : 0 }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
            marginBottom: 4,
          }}>
            <span style={{
              fontSize: 10.5, fontWeight: 750, letterSpacing: "0.08em",
              textTransform: "uppercase", color: t.isDark ? "#C4B5FD" : "#4273D1",
            }}>Announcement</span>
            {when ? (
              <span style={{ fontSize: 11.5, color: t.c.textTertiary }}>{when}</span>
            ) : null}
          </div>
          <div style={{
            fontSize: 16.5, fontWeight: 720, letterSpacing: "-0.03em",
            lineHeight: 1.3, color: t.c.text,
          }}>{announcement.title}</div>
          <p style={{
            margin: "6px 0 0", fontSize: 13.5, lineHeight: 1.55,
            color: t.c.textSecondary, whiteSpace: "pre-wrap", maxWidth: 720,
          }}>{announcement.body}</p>
        </div>
        {canDismiss ? (
          <button
            type="button"
            onClick={dismiss}
            disabled={closing}
            aria-label="Close announcement"
            title="Close — this announcement will not show again"
            style={{
              position: "absolute", top: 0, right: 0,
              width: 28, height: 28, borderRadius: 8,
              border: `1px solid ${t.c.border}`, background: t.c.surface,
              color: t.c.textTertiary, display: "grid", placeItems: "center",
              cursor: closing ? "wait" : "pointer",
            }}
          >
            <X size={14} />
          </button>
        ) : null}
      </div>
    </aside>
  );
}

export default AnnouncementBanner;
